import { ExternalLink, FileSpreadsheet, History } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { FolderTabs } from '../../../../components/FolderTabs';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { PpaLineItemModal } from '../components/PpaLineItemModal';
import { PpaLineItemsModal } from '../components/PpaLineItemsModal';
import { PpaLineItemsPanel } from '../components/PpaLineItemsPanel';
import { PpaModal } from '../components/PpaModal';
import { ppasApi } from '../ppasApi';
import { type PpaDetail, type PpaLineItemDto } from '../ppaTypes';

/**
 * One Program, Project or Activity, and the budgeted lines beneath it.
 *
 * **The HEADER layout, because a PPA reads as one continuous thing** — its own facts and
 * then what it is asking for, which is the same call the issuance profile takes one suite
 * along. A sidebar of panels would be a picker over four short sections.
 *
 * **TWO LEVELS OF `<FolderTabs>`: `Info · Line Items`, and the EXPENSE CLASS inside Line
 * Items.** That is the reference's own structure, read off its source rather than inferred — it
 * nests `<FolderTabs>` for the classes and sets `activeTab` to `line-items`. From 06 Oct 2026 the
 * class is the PPA's FUND SOURCE and every line follows it, so the second strip carries ONE tab:
 * the PPA's own class.
 *
 * It shipped with all four on ONE strip, argued from §6's "the folder nav is the primary tab
 * pattern, ONE level, and pills are what goes below it". That reading flattened a level of
 * the hierarchy away to keep a rule about depth, which is the rule defeating its own purpose.
 * A second `<PillNav>` component was then written for the second level and is deleted: the
 * product already has the component the reference uses for it.
 *
 * **The class owns its total, under its own table.** A `<PreviewTable
 * variant="table-display">` below the grid, which is the SALN modal's shape: a read-only
 * fact being shown rather than a value being asked for, sitting below the rows it adds up.
 * The PPA's overall total is the header's, so no tab states it twice.
 */
export function PpaProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<PpaDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    LANDS ON LINE ITEMS, which is the reference's own `useState<PpaTab>('line-items')`.

    `Info` is still the tab DECLARED first — that is what `<ProfileHeaderShell>` checks in
    development, and it is about declaration order rather than where the page opens. This is
    the exception the document profile already takes: a record somebody opens to READ its
    content lands on the content, and a PPA is opened to read what it is asking for.
  */
  const [tab, setTab] = useState<string>('line-items');

  const [editingPpa, setEditingPpa] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingLine, setEditingLine] = useState<PpaLineItemDto | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'delete-line' | 'restore-line';
    lines: PpaLineItemDto[];
  } | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      ppasApi
        // `includeDeleted` is what makes a deleted line reachable, and therefore restorable.
        .get(id, showDeleted, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setDetail(next);
          setError(null);

          /*
            The plan's adopted goals, for the EDIT form's picker — the constraint this module
            exists for, and the one thing the PPA's own record cannot answer. Fails SOFT: a
            failed fetch leaves the picker empty rather than blanking a page that has just
            read the record successfully.
          */
          /*
            NO PLAN FETCH. This read the plan to hand its adopted goals to the editor; the
            editor reads the catalog itself now, so a second request for a list nobody uses is
            gone with it.
          */
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this PPA.', 0));
        });

      /*
        NO `loading` flag. The page is in its first-load state exactly while `detail` is
        null and nothing has failed, which is what the early returns below read — a second
        boolean saying the same thing is one more state that can disagree with the record.
        A REFRESH deliberately leaves the page up: a write re-reads the record and the
        reader keeps looking at the last good answer until the newer one lands.
      */
    },
    [id, showDeleted],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function runConfirmed() {
    if (!confirming || !id) return;

    setRunning(true);
    setActionError(null);

    try {
      for (const line of confirming.lines) {
        if (confirming.kind === 'delete-line') {
          await ppasApi.deleteLineItem(id, line.id);
        } else {
          await ppasApi.restoreLineItem(id, line.id);
        }
      }

      setConfirming(null);
      load();
    } catch (caught) {
      setActionError(
        caught instanceof ApiError ? caught : new ApiError('Could not complete that.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  const ppa = detail?.ppa ?? null;

  const lockedReason = ppa?.isEditable
    ? null
    : 'This plan is closed to changes, so its PPAs and their budgeted lines can no longer be edited.';

  /** Every line follows the PPA's one class, so the panel takes them all, in server order. */
  const lines = useMemo(
    () => [...(detail?.lineItems ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [detail],
  );

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Investment Planning',
    { label: 'Budget Plans', to: '/procurement/investment-planning/budget-plans' },
    ...(ppa
      ? [
          {
            label: ppa.budgetPlanNumber,
            to: `/procurement/investment-planning/budget-plans/${ppa.budgetPlanId}`,
          },
          {
            label: ppa.orgUnitName,
            to: `/procurement/investment-planning/budget-plans/${ppa.budgetPlanId}/departments/${ppa.orgUnitId}`,
          },
        ]
      : []),
    ppa?.ppaNumber ?? 'PPA',
  ];

  const actions = useMemo<MenuItem[]>(
    () => [
      {
        key: 'history',
        label: 'History Logs',
        icon: <History className="h-4 w-4" aria-hidden="true" />,
        permission: 'audit-logs.read',
        onClick: () => setShowHistory(true),
      },
    ],
    [],
  );

  // Guarded on having NOTHING to show, never on `error` alone: once a record is up it is
  // still the last good answer, and a refused write goes to the banner.
  if (error && !detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => load()}
        />
      </div>
    );
  }

  if (!ppa || !detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-28 w-full rounded-panel" />
          <Skeleton className="h-96 w-full max-w-4xl rounded-panel" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="ppa-profile"
        header={
          <ProfileHeader
            id="ppa-profile-header"
            icon={<FileSpreadsheet className="h-6 w-6" aria-hidden="true" />}
            title={ppa.ppaNumber}
            meta={[ppa.name, ppa.orgUnitName, ppa.budgetPlanNumber]}
            secondary={[
              `${ppa.lineItemCount} budgeted line${ppa.lineItemCount === 1 ? '' : 's'}`,
              formatMoney(ppa.total),
              ppa.expenseClassLabel,
            ]}
            status={
              <StatusText tone={ppa.isActive ? 'active' : 'inactive'}>
                {ppa.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={ppa.createdByName}
            createdAt={ppa.createdAt}
            actions={
              <Menu
                id="ppa-profile-settings"
                items={actions}
                trigger={({ toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-label="PPA options"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                  >
                    <History className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        /*
          A banner for EVERY state, not only the bad ones — the strip a reader only ever sees
          when something is wrong is the strip they learn to skip, and then it is missed on
          the day it matters. The ordinary case says what the PPA is for and what can still
          be done to it.
        */
        banner={
          actionError ? (
            <ProfileBanner error message={actionError.message} id="ppa-profile-failure" />
          ) : !ppa.isActive ? (
            <ProfileBanner
              id="ppa-profile-deleted"
              message="This PPA is deleted. Its budgeted lines are kept exactly as they were, so a restore brings back the whole proposal."
            />
          ) : lockedReason ? (
            <ProfileBanner id="ppa-profile-locked" message={lockedReason} />
          ) : (
            <ProfileBanner
              id="ppa-profile-open"
              message={
                ppa.expenseClass == null
                  ? 'This PPA has no fund source yet. Choose one with Edit on the PPA card under Info before adding its lines.'
                  : ppa.lineItemCount === 0
                  ? `This PPA is filed under ${ppa.expenseClassLabel} and has nothing budgeted yet. Add its lines from the Line Items tab.`
                  : `This plan is open, so ${ppa.ppaNumber} and its budgeted lines can still be corrected.`
              }
            />
          )
        }
        /*
          TWO LEVELS, which is the reference's own shape: `Info · Line Items` on the folder
          strip, and the three expense classes as the sub-navigation INSIDE Line Items.

          All four sat on ONE folder strip, on the argument that `<FolderTabs>` is the primary
          pattern one level deep and pills go below it — so putting the classes on the one
          strip kept that rule with the same information. It does not: it flattens a level of
          the hierarchy away, and the reference has two. **Both levels are `<FolderTabs>`**,
          read off the reference's own source, which nests them here.
        */
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'line-items',
            label: (
              <>
                Line Items <CountBadge count={ppa.lineItemCount} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="ppa-details"
              title="PPA"
              onEdit={() => setEditingPpa(true)}
              editPermission="ppas.update"
              editDisabledReason={lockedReason}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'PPA number', value: ppa.ppaNumber, mono: true },
                  { label: 'Name', value: ppa.name },
                  { label: 'Budget plan', value: ppa.budgetPlanNumber, mono: true },
                  { label: 'Department', value: ppa.orgUnitName },
                  { label: 'Fund source', value: ppa.expenseClassLabel },
                  { label: 'Expected outcome', value: ppa.expectedOutcome },
                ]}
              />
            </ProfileCard>

            {/*
              THE GOAL'S OWN TRIAD, and it is the GOAL's rather than the PPA's — resolved
              live through the join, so correcting the catalog corrects every PPA aligned to
              it. The reference stores a copy of all five values on the PPA and again on
              every allotment release, which is one fact in four slots.
            */}
            {/*
              NO Edit, and that is `<ProfileCard>`'s THIRD exemption read literally: a card
              over ANOTHER module's record links out to that record instead of editing it in
              place. Every row here belongs to the goal catalog, which is maintained under
              Investment Planning → Settings — so the cog's one entry opens the goal, under
              the key of what it OPENS.

              **The ASSOCIATION is still this module's own field and IS editable** — which
              goal this PPA aligns to is a column on the PPA, corrected from the card above
              through the same picker that set it. Read the exemption as "no edit of the
              other module's fields", never as "no cog".
            */}
            <ProfileCard
              id="ppa-goal"
              title="Goal alignment"
              menuItems={
                ppa.goalId
                  ? [
                      {
                        key: 'open-goal',
                        label: 'Open goal',
                        icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />,
                        permission: 'budget-goals.read',
                        onClick: () =>
                          navigate(
                            `/procurement/investment-planning/settings/goals/${ppa.goalId}`,
                          ),
                      },
                    ]
                  : []
              }
              empty={
                ppa.goalId
                  ? undefined
                  : {
                      title: 'No goal aligned',
                      description:
                        'This PPA advances none of the goals its plan adopted. Point it at one from the PPA card above.',
                    }
              }
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Sector', value: ppa.sectorName },
                  { label: 'Sub-sector', value: ppa.subSectorName },
                  { label: 'Goal', value: ppa.goalName },
                  { label: 'Objective', value: ppa.goalObjective },
                  { label: 'Strategy', value: ppa.goalStrategy },
                ]}
              />
            </ProfileCard>

            {/* Never editable, at any permission level — a record of what happened. */}
            <ProfileCard id="ppa-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: ppa.createdByName },
                  {
                    label: 'Created',
                    value: <time dateTime={toIso(ppa.createdAt)}>{formatDateTime(ppa.createdAt)}</time>,
                    mono: true,
                  },
                  { label: 'Updated by', value: ppa.updatedByName },
                  {
                    label: 'Latest update',
                    value: ppa.updatedAt ? (
                      <time dateTime={toIso(ppa.updatedAt)}>{formatDateTime(ppa.updatedAt)}</time>
                    ) : null,
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : null}

        {tab === 'line-items' ? (
          <div className="flex h-full min-w-0 flex-col gap-3">
            {/*
              THE SECOND LEVEL, and it is `<FolderTabs>` — the reference's own component for
              it, and this product's own too.

              It was a `<PillNav>` I wrote for this page, on §6's "pill badges are the
              sub-navigation one level deeper; don't mix folder tabs and pills at the same
              depth". These are not at the same depth — that is the whole point of the second
              strip — and the reference nests its folder tabs here rather than reaching for a
              second treatment. A component invented where the product already has one is the
              drift the golden rule names, so `<PillNav>` is deleted rather than kept beside
              it.

              ONE TAB, from 06 Oct 2026: the class is the PPA's fund source and every line
              follows it, so the strip names that class and nothing else — the other two would
              be tabs that can only ever read zero. A PPA with no fund source yet draws no strip;
              the banner says what to do. The count is the line count the header already reads.
            */}
            {ppa.expenseClass != null ? (
              <FolderTabs<string>
                ariaLabel="Expense class"
                paddingX="px-0"
                activeKey={String(ppa.expenseClass)}
                onChange={() => undefined}
                tabs={[
                  {
                    key: String(ppa.expenseClass),
                    label: (
                      <span className="inline-flex items-center gap-1.5">
                        {ppa.expenseClassLabel}
                        <CountBadge count={ppa.lineItemCount} tone="active" />
                      </span>
                    ),
                  },
                ]}
              />
            ) : null}

            <PpaLineItemsPanel
              expenseClass={ppa.expenseClass ?? null}
              lines={lines}
              ceilingApproved={ppa.ceilingApproved ?? null}
              ceilingRemaining={ppa.ceilingRemaining ?? null}
              lockedReason={lockedReason}
              showDeleted={showDeleted}
              onToggleDeleted={() => setShowDeleted((current) => !current)}
              onAdd={() => setAdding(true)}
              onEdit={(line: PpaLineItemDto) => setEditingLine(line)}
              onDelete={(selected: PpaLineItemDto[]) =>
                setConfirming({ kind: 'delete-line', lines: selected })
              }
              onRestore={(line: PpaLineItemDto) =>
                setConfirming({ kind: 'restore-line', lines: [line] })
              }
            />
          </div>
        ) : null}
      </ProfileHeaderShell>

      {editingPpa ? (
        <PpaModal
          open
          budgetPlanId={ppa.budgetPlanId}
          planNumber={ppa.budgetPlanNumber}
          orgUnitId={ppa.orgUnitId}
          orgUnitName={ppa.orgUnitName}
          editing={ppa}
          onClose={() => setEditingPpa(false)}
          onSaved={() => {
            setEditingPpa(false);
            load();
          }}
        />
      ) : null}

      {adding && id && ppa.expenseClass != null ? (
        <PpaLineItemsModal
          open
          ppaId={id}
          ppaNumber={ppa.ppaNumber}
          ppaName={ppa.name}
          expenseClass={ppa.expenseClass}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            load();
          }}
        />
      ) : null}

      {editingLine && id ? (
        <PpaLineItemModal
          open
          ppaId={id}
          expenseClass={ppa.expenseClass ?? null}
          line={editingLine}
          onClose={() => setEditingLine(null)}
          onSaved={() => {
            setEditingLine(null);
            load();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => setConfirming(null)}
          onConfirm={() => void runConfirmed()}
          title={
            confirming.kind === 'restore-line'
              ? 'Restore this line'
              : confirming.lines.length === 1
                ? 'Delete this line'
                : `Delete ${confirming.lines.length} lines`
          }
          danger={confirming.kind === 'delete-line'}
          requireConfirm={confirming.kind === 'delete-line'}
          requireCredential={confirming.kind === 'delete-line'}
          loading={running}
          id={`ppa-line-${confirming.kind}-dialog`}
          body={
            confirming.kind === 'delete-line' ? (
              <div className="flex flex-col gap-2">
                <p>
                  {/* Named where a reader can still check them, counted where they cannot. */}
                  {confirming.lines.length <= 5
                    ? confirming.lines.map((l) => l.item).join(', ')
                    : `${confirming.lines.length} lines`}{' '}
                  {confirming.lines.length === 1 ? 'is' : 'are'} kept and can be restored —
                  nothing here is destroyed.
                </p>
                <p>
                  This PPA&rsquo;s{' '}
                  {ppa.expenseClassLabel ?? 'budgeted'} total drops by{' '}
                  {formatMoney(
                    confirming.lines.reduce((sum, l) => sum + l.effectiveAmount, 0),
                  )}
                  .
                </p>
                {/*
                  The two most confusable states, said outright — the same split the
                  disciplinary record's delete dialog draws. Deleting is a correction to the
                  PROPOSAL; excluding is a reviewer's verdict, and it is not this reader's to
                  make.
                */}
                <p className="text-slate-500">
                  Deleting a line is not the same as a reviewer EXCLUDING it. An excluded line
                  stays on the proposal and counts toward nothing; a deleted one leaves it.
                </p>
              </div>
            ) : (
              <p>
                {confirming.lines[0]?.item} returns to this PPA and counts toward its{' '}
                {ppa.expenseClassLabel ?? 'budgeted'} total again.
              </p>
            )
          }
        />
      ) : null}

      {showHistory && id ? (
        <HistoryLogsModal
          open
          onClose={() => setShowHistory(false)}
          module="ppas"
          entityId={id}
        />
      ) : null}
    </div>
  );
}
